import {
	useApiGetEntityCounts,
	useApiHasPreviewAccess,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useIntegration,
	useSuspenseFeatureAccess,
	useUpdateSecodaEntity,
} from '../../api';
import useSuspenseSecodaEntity from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import {
	getDefaultTab,
	getOrderedTabs,
} from '../../components/EntityPageLayout/utils';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import type { OverviewPageTab } from '../../components/Overview/OverviewPageTabs';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import UrlPreview from '../../components/Preview/UrlPreview';
import { QueryList } from '../../components/QueryListV2/QueryList';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import MonitorsTab from '../TableEntityPage/TableEntityTabs/MonitorsTab';
import type { DjangoValueType } from '../TemplatePage/types';
import ChartsTab from './DashboardEntityTabs/ChartsTab';
import SigmaPagesTab from './DashboardEntityTabs/SigmaPagesTab/SigmaPagesTab';

const InnerDashboardPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const {
		lineageAccess,
		monitoringAccess,
		resourcePreviewAccess,
		secodaCoreAccess,
	} = useSuspenseFeatureAccess();

	const { data: dashboard } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('dashboard_entity/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSecodaEntity({});

	const updateDashboard = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				mutateAsync({
					data: {
						id: dashboard!.id,
						[key]: value,
					},
				});
				trackEvent('dashboard/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, dashboard, user, workspace]
	);

	const { data: integration } = useIntegration({
		id: dashboard?.integration || '',
		options: {
			enabled: !!dashboard?.integration,
		},
	});

	const { data: previewPermissionGranted } = useApiHasPreviewAccess(
		{
			pathParams: {
				integrationId: dashboard?.integration ?? '',
			},
		},
		{
			enabled: !!dashboard?.integration,
		}
	);

	const { hasPermission: hasQueriesAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Queries.Read',
	});

	const { hasPermission: hasQuestionsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Questions.Read',
	});

	// Hex dashboards are self-contained projects that don't have separate chart entities.
	// Snowflake charts are disabled for streamlit apps.
	// DBT charts are disabled for exposures.
	const integrationDefinition = integrationList.find(
		(def) => def.type === integration?.type
	);
	const supportsChartsTab = integrationDefinition?.supportsChartsTab ?? false;
	const showChartsTab = supportsChartsTab;

	const showSigmaPagesTab =
		integration?.type === 'sigma' && dashboard?.native_type === 'workbook';

	const showPreviewTab =
		integration?.support_dashboard_preview &&
		!!previewPermissionGranted &&
		resourcePreviewAccess;

	const documentationFirst = (dashboard?.definition?.length || 0) > 2;

	const navigateToTab = useTabNavigate();

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('dashboard/lineage/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	const { data: entityCounts, isLoading } = useApiGetEntityCounts(
		{
			queryParams: {
				entity_id: id || '',
			},
		},
		{
			enabled: !!id,
		}
	);

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			dashboard.id,
			dashboard.published,
			EntityType.dashboard,
			dashboard.native_type
		);

	if (!dashboard) {
		return <EntityPageLayoutSkeleton />;
	}

	const tabs: OverviewPageTab[] = useMemo(
		() => [
			...(showPreviewTab
				? [
						{
							value: 'preview',
							label: 'Preview',
							component: (
								<UrlPreview
									integration={integration?.type as string}
									entity={dashboard}
								/>
							),
						},
					]
				: []),
			{
				value: 'lineage',
				label: 'Lineage',
				condition: !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'monitors',
				label: 'Monitors',
				condition: !!monitoringAccess,
				component: (
					<MonitorsTab
						integrationId={dashboard.integration}
						upstreamOf={dashboard.id}
						canCreateMonitors={true}
					/>
				),
			},
			{
				value: 'charts',
				label: 'Charts',
				condition: showChartsTab && !showSigmaPagesTab,
				component: <ChartsTab id={dashboard.id} />,
			},
			{
				value: 'pages',
				label: 'Pages',
				condition: showSigmaPagesTab,
				component: <SigmaPagesTab id={dashboard.id} />,
			},
			{
				value: 'queries',
				label: 'Queries',
				condition:
					!isLoading && Boolean(entityCounts?.query_count) && hasQueriesAccess,
				component: <QueryList entity={dashboard} />,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: hasQuestionsAccess && !!secodaCoreAccess,
				component: <EntityDiscussions entity={dashboard} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={dashboard} />,
			},
		],
		[
			showPreviewTab,
			integration?.type,
			dashboard,
			showChartsTab,
			showSigmaPagesTab,
			isLoading,
			entityCounts?.query_count,
			hasQueriesAccess,
			hasQuestionsAccess,
			lineageAccess,
			monitoringAccess,
			secodaCoreAccess,
		]
	);

	const orderedTabs: OverviewPageTab[] = useMemo(
		() => getOrderedTabs(tabs, documentationFirst),
		[tabs, documentationFirst]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(orderedTabs), [orderedTabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={dashboard.id}
			name="Dashboard"
			isReadOnlyTitle
			entity={dashboard}
			icon={<EntityLogo entity={dashboard} size={32} />}
			updateEntity={updateDashboard}
			withDescription
			{...getEntityPageSidebarProps(EntityType.dashboard)}
		>
			<OverviewPageTabs
				key={dashboard.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={orderedTabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// By memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function DashboardPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerDashboardPage id={id} />;
}
